import { Toast, Button } from '@/shared/ui/primitives';
import { AlertCircle, CheckCircle, Info, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './GlobalToast.module.css';
import { subscribeToToast, type ToastEvent } from '@/shared/lib/toastEvents';

type ActiveToast = ToastEvent & { key: number };

const icons: Record<ToastEvent['type'], LucideIcon> = {
  success: CheckCircle,
  info: Info,
  error: AlertCircle,
};

export function GlobalToast() {
  const [toast, setToast] = useState<ActiveToast | null>(null);
  const nextKey = useRef(0);

  useEffect(
    () =>
      subscribeToToast((event) => {
        setToast({ ...event, key: nextKey.current++ });
      }),
    []
  );

  const dismiss = useCallback((key: number) => {
    setToast((current) => (current?.key === key ? null : current));
  }, []);

  if (!toast) return null;
  const Icon = icons[toast.type];

  return (
    <Toast
      key={toast.key}
      icon={<Icon className={styles.icon} size={20} aria-hidden="true" />}
      duration={toast.durationMs ?? 4000}
      onDismiss={() => dismiss(toast.key)}
      className={`${styles.snackbar} ${styles[toast.type]}`}
      role={toast.type === 'error' ? 'alert' : 'status'}
      action={
        toast.action ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              toast.action?.onClick();
              dismiss(toast.key);
            }}
          >
            {toast.action.label}
          </Button>
        ) : undefined
      }
    >
      {toast.message}
    </Toast>
  );
}
